import * as React from 'react';
import { Input, type InputProps } from '@/components/ui/input';

/**
 * Input for amounts and percentages.
 *
 * Deliberately not `type="number"`. A focused number input treats the mouse
 * wheel and the Up/Down keys as value steppers, so scrolling the page or
 * brushing an arrow key silently edits the figure underneath the cursor — with
 * `step="0.01"` that is a one-paisa change nobody notices until it has reached
 * the ledger. `inputMode="decimal"` keeps the numeric keypad on mobile without
 * bringing the stepper along.
 *
 * Typing is filtered on keydown, so a rejected character never reaches the
 * value and the caret never jumps. Anything arriving another way — paste,
 * drag-and-drop, autofill — is parsed on change instead, which also means a
 * pasted "Rs. 1,350,000.00" lands as 1350000.00 rather than being thrown away
 * wholesale the way `type="number"` throws it away.
 */

// Thousands separators that turn up in pasted amounts: comma, any whitespace,
// non-breaking and narrow no-break space (emitted by some locale formatters),
// and apostrophe.
const GROUPING = '[,\\s\\u00A0\\u202F\']';
const PASTED_NUMBER = new RegExp(`\\d(?:${GROUPING}*\\d)*(?:\\.\\d+)?|\\.\\d+`);

/**
 * Pulls the first number out of human-entered text: "Rs. 1,350,000.00" and
 * "₹1 350 000" both yield their digits, "abc" yields nothing. Used only for
 * pasted text, never for keystrokes — it cannot represent a half-typed value
 * like "12." and would fight the caret if it ran while someone was typing.
 */
function parsePasted(raw: string, allowNegative: boolean): string {
  const negative = allowNegative && raw.includes('-');
  const match = raw.match(PASTED_NUMBER);
  if (!match) return '';
  const digits = match[0].replace(new RegExp(GROUPING, 'g'), '');
  return negative ? `-${digits}` : digits;
}

/**
 * Safety net for values that change without a keystroke or a paste. Strips
 * anything that is not a digit or a decimal point and collapses repeated
 * points, while leaving legitimate half-typed values ("", ".", "12.") alone.
 */
function filterValue(raw: string, allowNegative: boolean): string {
  const negative = allowNegative && raw.startsWith('-');
  const [whole, ...fractions] = raw.replace(/[^\d.]/g, '').split('.');
  const joined = fractions.length ? `${whole}.${fractions.join('')}` : whole;
  return negative ? `-${joined}` : joined;
}

/** Tidies a half-typed value once the field loses focus: "12." → "12", "." → "". */
function normalizeOnBlur(value: string): string {
  if (value === '.' || value === '-' || value === '-.') return '';
  return value.endsWith('.') ? value.slice(0, -1) : value;
}

export interface DecimalInputProps extends Omit<InputProps, 'type' | 'inputMode' | 'step'> {
  /** Permit a leading minus. Off by default — every amount in this app is >= 0. */
  allowNegative?: boolean;
}

export const DecimalInput = React.forwardRef<HTMLInputElement, DecimalInputProps>(
  ({ allowNegative = false, onKeyDown, onChange, onPaste, onBlur, ...props }, ref) => {
    // Set by onPaste, read by the change it triggers. Pasted text needs parsing;
    // typed text is already valid and must be left exactly as the user typed it.
    const pasted = React.useRef(false);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      onKeyDown?.(e);
      if (e.defaultPrevented) return;
      // Shortcuts (select-all, copy, paste, undo) and every named key —
      // Backspace, Delete, Tab, Home, the arrows — pass through untouched.
      if (e.ctrlKey || e.metaKey || e.altKey || e.key.length > 1) return;
      if (/\d/.test(e.key)) return;

      const el = e.currentTarget;
      const selected = el.value.slice(el.selectionStart ?? 0, el.selectionEnd ?? 0);
      // A second decimal point is allowed only when it replaces the first.
      if (e.key === '.' && (!el.value.includes('.') || selected.includes('.'))) return;
      if (allowNegative && e.key === '-' && el.selectionStart === 0 && !el.value.includes('-')) return;

      e.preventDefault();
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      onPaste?.(e);
      pasted.current = true;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const wasPasted = pasted.current;
      pasted.current = false;
      const clean = wasPasted
        ? parsePasted(e.target.value, allowNegative)
        : filterValue(e.target.value, allowNegative);
      // Only assigned when something was actually stripped, so ordinary typing
      // never has its caret shunted to the end of the field.
      if (clean !== e.target.value) e.target.value = clean;
      onChange?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      const clean = normalizeOnBlur(e.target.value);
      if (clean !== e.target.value) {
        e.target.value = clean;
        // The field is uncontrolled, so react-hook-form only learns the tidied
        // value through its change handler — blur alone would leave it stale.
        onChange?.(e as unknown as React.ChangeEvent<HTMLInputElement>);
      }
      onBlur?.(e);
    };

    return (
      <Input
        {...props}
        ref={ref}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onChange={handleChange}
        onBlur={handleBlur}
      />
    );
  },
);
DecimalInput.displayName = 'DecimalInput';
